/**
 * API Error Utilities — Donor Portal
 *
 * Provides helpers to classify API errors and extract user-friendly messages.
 * Uses vue-i18n (via the app's i18n singleton) for translations, unlike
 * tms-app-soho which uses i18next directly.
 */
import axios from 'axios';
import { i18n } from '@/core/plugins/i18n';

/**
 * Known technical patterns in error messages mapped to i18n keys.
 */
const TECHNICAL_MESSAGE_MAP: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /P2025/i, key: 'apiErrors.recordNotFound' },
  { pattern: /ECONNREFUSED/i, key: 'apiErrors.connectionFailed' },
  { pattern: /ETIMEDOUT|ECONNABORTED/i, key: 'apiErrors.requestTimeout' },
  { pattern: /network\s?error/i, key: 'apiErrors.networkError' },
  { pattern: /Route\s+(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS):/i, key: 'apiErrors.routeNotFound' },
  // tms-api-donor: milk provider not found (all three services)
  { pattern: /No milk provider found/i, key: 'apiErrors.milkProviderNotFound' },
  // tms-api-donor: person_type mismatch (donor portal accessed by a non-donor account)
  { pattern: /Only donors are accepted/i, key: 'donor.errors.notDonorType' },
  // Cognito InvalidPasswordException (policy failure or compromised credential detection)
  { pattern: /does not meet security requirements/i, key: 'apiErrors.invalidPassword' },
];

/**
 * Map HTTP status codes to i18n keys for errors that lack a server-provided message.
 */
const STATUS_CODE_MAP: Record<number, string> = {
  500: 'apiErrors.serverError',
  502: 'apiErrors.serverError',
  503: 'apiErrors.serviceUnavailable',
  429: 'apiErrors.tooManyRequests',
};

/**
 * Regex matching UUIDs (hyphenated or non-hyphenated) that may appear in error messages.
 */
const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|\b[0-9a-f]{32}\b/gi;

/**
 * Translate a dotted i18n key using the vue-i18n global instance.
 */
function t(key: string): string {
  return (i18n.global as any).t(key);
}

/**
 * Check if an error is a 404 Not Found response.
 */
export function isNotFoundError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

/**
 * Check if an error is a 404 that indicates a resource is not yet configured.
 */
export function isNotConfiguredError(err: unknown): boolean {
  return isNotFoundError(err);
}

/**
 * Sanitize a technical error message into something a non-technical user can understand.
 * - Strips UUIDs that are meaningless to end users
 * - Replaces known technical codes with translated friendly text
 * - Cleans up leftover artifacts (double spaces, trailing prepositions)
 */
function sanitizeMessage(message: string): string {
  if (!message) return message ?? '';

  for (const { pattern, key } of TECHNICAL_MESSAGE_MAP) {
    if (pattern.test(message)) {
      return t(key) ?? message;
    }
  }

  // Strip UUIDs and clean up surrounding text
  let cleaned = message.replace(UUID_PATTERN, '').trim();
  // Remove dangling prepositions/articles left after UUID removal
  cleaned = cleaned.replace(/\b(for|with id|id:|id)\s*$/i, '').trim();
  // Collapse multiple spaces
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  // Remove trailing punctuation artifacts
  cleaned = cleaned.replace(/[:\s"']+$/, '').trim();

  return cleaned || message;
}

/**
 * Extract a user-friendly error message from an error.
 *
 * Priority:
 * 1. Server-provided message from the API response body
 * 2. Sanitized technical message (if it contains codes like P2025, ECONNREFUSED)
 * 3. Provided fallback (i18n key — resolved via vue-i18n)
 *
 * @param err - The caught error
 * @param fallback - i18n key or pre-translated string to use if nothing better is available
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  // Resolve fallback: if it looks like an i18n key (contains a dot, no spaces), translate it
  const resolvedFallback =
    fallback.includes('.') && !fallback.includes(' ') ? t(fallback) ?? fallback : fallback;

  let message = resolvedFallback;

  if (axios.isAxiosError(err)) {
    const serverMessage =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message;

    if (serverMessage && serverMessage !== 'Request failed with status code ' + err.response?.status) {
      message = serverMessage;
    } else if (err.response?.status && STATUS_CODE_MAP[err.response.status]) {
      return t(STATUS_CODE_MAP[err.response.status]) ?? resolvedFallback;
    }
  } else if (err instanceof Error && err.message) {
    message = err.message;
  } else if (typeof err === 'string' && err.length > 0) {
    message = err;
  }

  return sanitizeMessage(message);
}

/**
 * Handle a 404 error silently — returns true if the error was a 404 (caller should
 * treat as empty/not-configured), false if it's a different error that needs handling.
 */
export function handleSilent404(err: unknown, context: string): boolean {
  if (isNotFoundError(err)) {
    console.info(`[${context}] Not configured (404) — using empty defaults`);
    return true;
  }
  return false;
}
